import { Component } from '@theme/component';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
  SlideshowSelectEvent,
} from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(this.slideIndexFromZoomIndex(event.detail.index), undefined, { animate: false });
  };

  /**
   * The zoom dialog only lists product media, so slide indices and zoom indices diverge when a
   * metafield video (e.g. 360°) is positioned within the gallery. Slides carry their zoom index.
   *
   * @param {number} slideIndex
   * @returns {number}
   */
  zoomIndexFromSlideIndex(slideIndex) {
    const zoomIndex = this.slideshow?.slides?.[slideIndex]?.getAttribute('data-zoom-index');

    return zoomIndex == null ? slideIndex : Number(zoomIndex);
  }

  /**
   * @param {number} zoomIndex
   * @returns {number}
   */
  slideIndexFromZoomIndex(zoomIndex) {
    const slides = this.slideshow?.slides;
    if (!slides?.length) return zoomIndex;

    const slideIndex = Array.from(slides).findIndex(
      (slide) => slide.getAttribute('data-zoom-index') === String(zoomIndex)
    );

    return slideIndex === -1 ? zoomIndex : slideIndex;
  }

  /**
   * When the visible slide changes, pause video/model playback in other slides so audio
   * and CPU work do not continue (e.g. metafield 360° embeds outside deferred-media).
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const activeSlide = event.detail?.slide;
    if (!(activeSlide instanceof Element)) return;

    const slides = this.slideshow?.slides;
    if (!slides?.length) return;

    for (const slide of slides) {
      if (slide === activeSlide) continue;
      this.#pauseMediaInSlide(slide);
    }
  };

  /**
   * @param {Element} slide
   */
  #pauseMediaInSlide(slide) {
    for (const el of slide.querySelectorAll('deferred-media, product-model')) {
      if ('pauseMedia' in el && typeof el.pauseMedia === 'function') {
        el.pauseMedia();
      }
    }

    for (const video of slide.querySelectorAll('video')) {
      video.pause();
    }

    for (const iframe of slide.querySelectorAll('iframe')) {
      if (iframe.closest('deferred-media')) continue;

      const src = iframe.getAttribute('src') || '';
      const win = iframe.contentWindow;
      if (!win) continue;

      if (iframe.dataset.videoType === 'youtube' || /youtube\.com|youtu\.be/i.test(src)) {
        win.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (iframe.dataset.videoType === 'vimeo' || /vimeo\.com|player\.vimeo\.com/i.test(src)) {
        win.postMessage('{"method":"pause"}', '*');
      }
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Opens the zoom dialog at the currently visible slide index (for mobile expand button).
   * Ensures the zoomed view shows the same image the user is viewing instead of always the first.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  openZoomAtCurrentSlide(event) {
    const slideshow = this.refs.slideshow;
    const index = slideshow?.current ?? 0;
    const slide = slideshow?.slides?.[index];
    if (
      slide?.classList.contains('product-media-container--video') ||
      slide?.classList.contains('product-media-container--external_video') ||
      slide?.classList.contains('product-media-container--video-360')
    ) {
      return;
    }
    this.zoom(this.zoomIndexFromSlideIndex(index), event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
